import React, { useEffect, useState } from 'react'
import API from '../../services/API'
import DocumentPreviewModal from '../Documents/DocumentPreviewModal'
// @MUI
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import IconButton from '@mui/material/IconButton'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import DeleteIcon from '@mui/icons-material/Delete'
import ErrorIcon from '@mui/icons-material/Error'
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import BigModal from '../Modals/BigModal'
import PDFViewer from '../Modals/PDFViewer'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Visibility } from '@mui/icons-material'
import { Divider } from '@mui/material'

// `small` renders a compact tile (card variant only) for dense lists where the
// file type icon shouldn't dominate.
const S3Document = ({ item, onDelete, onClick, title, showAsRow, small }) => {
  const [loading, setLoading] = useState(true)
  const fileName =
    typeof item === 'string'
      ? item.split('/').pop()
      : (item?.name ?? item?.s3key?.split('/').pop())
  const [file, setFile] = useState(null)
  const [fileUrl, setFileUrl] = useState(null)
  const [showIcon, setShowIcon] = useState(true)
  const [openPopup, setOpenPopup] = useState(false)
  const [downloadError, setDownloadError] = useState(false)
  const extensionsForIcon = ['pdf', 'doc', 'docx']

  const imageExtensions = ['jpeg', 'jpg', 'png', 'gif', 'webp', 'jfif']
  const pdfExtensions = ['pdf']
  const documentExtensions = ['doc', 'docx']
  const videoExtensions = ['mp4', 'mov', 'm4v', 'webm']

  const fileExt =
    typeof item === 'string'
      ? item.split('.').pop()?.toLowerCase()
      : item?.name?.split('.').pop()?.toLowerCase()

  const isImage = imageExtensions.includes(fileExt)
  const isPdf = pdfExtensions.includes(fileExt)
  const isVideo = videoExtensions.includes(fileExt)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    if (
      extensionsForIcon.includes(fileExt) ||
      isVideo ||
      typeof item === 'object'
    ) {
      setShowIcon(
        item?.localIcon ??
          (isVideo ? (
            <PlayCircleIcon
              sx={{
                width: small ? '40%' : '60%',
                height: small ? '40%' : '60%',
              }}
              color="primary"
            />
          ) : (
            <DocumentScannerIcon
              sx={{
                width: small ? '40%' : '80%',
                height: small ? '40%' : '80%',
              }}
              color="primary"
            />
          )),
      )
      setLoading(false)
    } else {
      API.post('/gets3downloadurl', {
        key: item,
        inlineView: true,
      })
        .then((res) => setFile(res.data))
        .catch(() => setDownloadError(true))
        .finally(() => setLoading(false))
    }
  }, [item])

  const handleDownload = async (e) => {
    e?.stopPropagation()

    try {
      const res = await API.post('/gets3downloadurl', {
        key: item?.s3key ?? item,
        inlineView: false,
        filename: title || fileName,
      })

      const link = document.createElement('a')
      link.href = res.data
      link.download = title || fileName
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch {
      alert('Failed to download the file.')
    }
  }

  const handleOnClick = async () => {
    if (onClick) {
      onClick()
      return
    }

    try {
      const res = await API.post('/gets3downloadurl', {
        key: item?.s3key ?? item,
        inlineView: true,
        // Videos stream/seek against the signed URL for the whole watch, so
        // the default 60s expiry cuts playback off partway through
        ...(isVideo && { expires: 3600 }),
      })

      setFileUrl(res.data)
      setOpenPopup(true)
    } catch {
      alert('Failed to load the document.')
    }
  }

  if (showAsRow) {
    return (
      <Box
        width="100%"
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
      >
        <Stack
          direction="row"
          gap={1}
          justifyContent="space-between"
          alignItems="center"
          onClick={handleOnClick}
          width="100%"
          padding={1}
          sx={{
            cursor: 'pointer',
            boxSizing: 'border-box',
            minWidth: 0, // IMPORTANT: allow the Stack to shrink inside parent
          }}
        >
          <Typography
            sx={{ minWidth: 0, flex: 1 }} // allow shrink
            noWrap
          >
            {title ?? fileName}
          </Typography>

          <Visibility />
        </Stack>

        {/* Kept outside the clickable row. The dialog portals to <body>, but
            React events still bubble up the component tree, so inside the
            Stack every click in the preview (Close, backdrop) re-ran
            handleOnClick and reopened it. */}
        <DocumentPreviewModal
          open={openPopup}
          onClose={() => setOpenPopup(false)}
          fileUrl={fileUrl}
          fileName={fileName}
          originalFileName={item?.name}
          onDownload={handleDownload}
          fileExt={fileExt}
          isImage={isImage}
          isPdf={isPdf}
          isVideo={isVideo}
        />
      </Box>
    )
  }
  return (
    <Badge
      badgeContent={
        onDelete && (
          <IconButton
            size="small"
            sx={{
              color: 'white',
              backgroundColor: '#D32F2F',
              '&:hover': {
                color: '#D32F2F',
                backgroundColor: '#F6F6F6',
              },
            }}
            onClick={() => onDelete(item?.s3key ?? item)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )
      }
    >
      <Card
        sx={{
          width: showAsRow ? '100%' : small ? 132 : 200,
          cursor: showIcon && 'pointer',
        }}
        onClick={() => {
          handleOnClick()
        }}
      >
        <Box
          sx={{
            height: small ? 88 : 140,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {loading ? (
            <Skeleton
              variant="rounded"
              height={small ? 88 : 140}
              width={small ? 132 : 200}
            />
          ) : downloadError ? (
            <ErrorIcon color="error" />
          ) : file ? (
            <img
              src={file}
              alt={fileName}
              style={{ objectFit: 'contain', maxHeight: '100%' }}
            />
          ) : (
            showIcon
          )}
        </Box>

        <CardContent
          sx={
            small
              ? { px: 1, py: 0.75, '&:last-child': { pb: 0.75 } }
              : undefined
          }
        >
          <Stack alignItems="center">
            {downloadError && (
              <Typography variant="caption">Unable to Download</Typography>
            )}
            <Typography variant="caption" textAlign="center">
              {title
                ? title
                : isVideo
                  ? 'Video'
                  : fileExt === 'pdf' || typeof item === 'object'
                    ? 'PDF'
                    : // Images and Word files used to sit over an empty
                      // caption strip
                      (fileExt?.toUpperCase() ?? '')}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
      {openPopup && (
        // <PDFViewer
        //   s3key={item}
        //   open={openPopup === true}
        //   onClose={() => setOpenPopup(false)}
        // />
        <DocumentPreviewModal
          open={openPopup}
          onClose={() => setOpenPopup(false)}
          fileUrl={fileUrl}
          fileName={fileName}
          originalFileName={item?.name}
          fileExt={fileExt}
          onDownload={handleDownload}
          isImage={isImage}
          isPdf={isPdf}
          isVideo={isVideo}
        />
      )}
    </Badge>
  )
}

export default S3Document
